"use client";

import type { ReviewItem } from "@/lib/api/projection-types";
import type { ContentQueueItem } from "@/lib/api";
import { LIFE_AREA_META } from "@/lib/lifeAreas";
import {
  deriveSourceFromUrl,
  formatSavedFrom,
  libraryItemSource,
  libraryTypeKey,
  LIBRARY_TYPE_LABEL,
} from "@/lib/libraryFormatters";
import { sanitizeSurfaceTitle } from "@/lib/savedResourceTitles";
import LibraryTypeIcon from "./LibraryTypeIcon";

interface Props {
  item: ReviewItem;
  detail?: ContentQueueItem;
  recommended: boolean;
  busy: boolean;
  onClose: () => void;
  onAddToTodo: (item: ReviewItem) => void;
  onAddToSchedule: (item: ReviewItem) => void;
}

export default function LibraryDetailModal({
  item,
  detail,
  recommended,
  busy,
  onClose,
  onAddToTodo,
  onAddToSchedule,
}: Props) {
  const title = sanitizeSurfaceTitle(item.title);
  const goalTitle = item.goal_title ? sanitizeSurfaceTitle(item.goal_title) : null;
  const areaMeta = item.goal_category ? LIFE_AREA_META[item.goal_category] : null;
  const typeKey = libraryTypeKey(item.content_type ?? detail?.content_type);
  const url = item.url ?? detail?.url ?? null;
  const source = deriveSourceFromUrl(url, "");
  const sourceKind = libraryItemSource(typeKey, Boolean(url));
  const savedFrom = formatSavedFrom(detail?.saved_at ?? null, sourceKind);
  const because = detail?.priority_reason ?? null;
  const description = (item.description ?? detail?.description ?? "").trim();

  return (
    <div
      data-testid="library-detail-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/25 backdrop-blur-[2px] px-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="library-detail-title"
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className={`h-2 rounded-t-2xl ${recommended ? "bg-amber" : "bg-forest-med"}`} />
        <div className="p-5">
          <div className="flex items-start justify-between gap-3">
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ${
                recommended ? "bg-amber/15 text-earth" : "bg-sage-pale text-forest-med"
              }`}
            >
              <LibraryTypeIcon type={typeKey} size={12} />
              {LIBRARY_TYPE_LABEL[typeKey]}
              {recommended && <span className="ml-1">· ★ Recommended</span>}
            </span>
            <button
              type="button"
              onClick={onClose}
              className="flex h-7 w-7 items-center justify-center rounded-full text-muted hover:bg-secondary hover:text-text"
              aria-label="Close"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          <h2 id="library-detail-title" className="mt-3 text-lg font-semibold leading-snug text-text">
            {title}
          </h2>

          {(areaMeta || goalTitle) && (
            <p className="mt-1.5 text-xs text-muted">
              {areaMeta && (
                <span className="capitalize">
                  {areaMeta.emoji} {areaMeta.label}
                </span>
              )}
              {areaMeta && goalTitle && " · "}
              {goalTitle && <span className="text-forest-med">{goalTitle}</span>}
            </p>
          )}

          {savedFrom && (
            <p className="mt-2 text-[11px] text-muted">{savedFrom}</p>
          )}

          {item.duration_min || detail?.duration_min ? (
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              <span className="rounded-full bg-secondary/60 px-2.5 py-1 text-[10px] font-semibold text-text-light">
                {item.duration_min ?? detail?.duration_min}m read / watch
              </span>
            </div>
          ) : null}

          {url && (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex max-w-full items-center gap-1.5 truncate rounded-xl border border-border bg-bg px-3 py-2 text-xs font-medium text-blue underline underline-offset-2 hover:border-forest-med/30 hover:text-forest-med"
              title={url}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
              <span className="truncate">{source || url}</span>
            </a>
          )}

          {description && (
            <div className="mt-3 max-h-[200px] overflow-y-auto whitespace-pre-wrap rounded-xl bg-bg px-3 py-2 text-[12.5px] leading-relaxed text-text-light">
              {description}
            </div>
          )}

          {because && (
            <p
              className={`mt-3 rounded-xl px-3 py-2 text-xs leading-relaxed ${
                recommended
                  ? "border-l-2 border-amber bg-sand-pale/70 italic text-earth"
                  : "bg-bg text-text-light"
              }`}
            >
              {recommended ? `Because: ${because}` : because}
            </p>
          )}

          <div className="mt-5 flex gap-2">
            <button
              type="button"
              onClick={() => onAddToTodo(item)}
              disabled={busy}
              className="flex-1 rounded-full bg-forest-med px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest disabled:opacity-60"
            >
              {busy ? "Adding..." : "Add to todo"}
            </button>
            <button
              type="button"
              onClick={() => onAddToSchedule(item)}
              disabled={busy}
              className="flex-1 rounded-full border border-border bg-white px-4 py-2.5 text-sm font-medium text-text transition-colors hover:border-forest-med/40 hover:text-forest-med disabled:opacity-60"
            >
              Add to schedule
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
